<template>
  <section v-if="isPending" class="py-24">
    <div class="mx-auto max-w-full px-24 md:max-w-[900px] md:px-32">
      <h2 class="heading-1 mb-8">{{ i18n(translations.title) }}</h2>

      <div class="flex justify-center py-40">
        <RevSpinner :alternative-text="i18n(translations.loading)" />
      </div>
    </div>
  </section>

  <section
    v-else-if="salesStatus === 'success' || tradeInsStatus === 'success'"
    class="py-24"
  >
    <div class="mx-auto max-w-full px-24 md:max-w-[900px] md:px-32">
      <h2 class="heading-1 mb-8">{{ i18n(translations.title) }}</h2>

      <div class="mb-16 flex gap-8 overflow-x-auto">
        <RevChip
          v-for="filter in ITEM_FILTERS"
          :key="filter.value"
          class="shrink-0"
          :label="i18n(filter.label)"
          :model-value="selectedFilter === filter.value"
          @click="selectedFilter = filter.value"
        />
      </div>

      <RevCard
        v-if="selectedFilter === 'protection-plans'"
        class="flex w-full flex-col gap-16 p-16"
      >
        <h3 class="heading-3">{{ i18n(translations.protectionPlansTitle) }}</h3>
        <p class="body-1 text-static-default-low">
          {{ i18n(translations.protectionPlansDescription) }}
        </p>
        <RevButton
          :to="protectionPlansTo"
          class="self-start"
          :tracking="{
            zone: 'help-center-manage-your-items',
            name: 'go-to-protection-plans',
          }"
          variant="primary"
        >
          {{ i18n(translations.protectionPlansCta) }}
        </RevButton>
      </RevCard>

      <RevCard
        v-else-if="!filteredOrderlines.length && !showSeeMore"
        class="flex w-full flex-col gap-16 p-16"
      >
        <h3 class="heading-3">
          {{ i18n(fallbackState.title) }}
        </h3>
        <p class="body-1 text-static-default-low">
          {{ i18n(fallbackState.description) }}
        </p>
        <RevButton
          :key="fallbackState.cta.id"
          :to="fallbackState.to"
          class="self-start"
          variant="primary"
        >
          {{ i18n(fallbackState.cta) }}
        </RevButton>
      </RevCard>

      <div v-else class="flex gap-16 overflow-x-auto pb-4">
        <HelpCenterItemCard
          v-for="orderline in visibleOrderlines"
          :key="orderline.key"
          class="w-[233px] shrink-0"
          v-bind="orderline.cardProps"
        />

        <RevCard
          v-if="showSeeMore"
          class="flex w-[256px] shrink-0 flex-col gap-12 p-24"
        >
          <RevIllustration
            image-class-names="h-[216px] w-full rounded-lg object-cover"
            :src="SEE_MORE_IMAGE_SRC"
            alt=""
            :height="216"
            :width="208"
            sizes="208px"
            loading="lazy"
          />
          <RevButton
            :key="selectedFilter === 'trade-ins' ? 'trade-ins' : 'orders'"
            :to="seeMoreTo"
            class="w-full"
            :tracking="{
              zone: 'help-center-manage-your-items',
              name: 'see-more-items',
            }"
            variant="primary"
          >
            {{ i18n(translations.seeMoreItems) }}
          </RevButton>
        </RevCard>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevChip } from '@ds/components/Chip'
import { RevIllustration } from '@ds/components/Illustration'
import { RevSpinner } from '@ds/components/Spinner'

import { useRouteLocationWithLocale } from '~/composables/useRouteLocationWithLocale'
import { CMS } from '~/scopes/cms/routes-names'
import { ROUTE_NAMES } from '~/scopes/dashboard/constants'
import { HOME } from '~/scopes/home/route-names'

import HelpCenterItemCard from '../HelpCenterItemCard/HelpCenterItemCard.vue'

import translations from './OrdersSection.translations'
import { MAX_VISIBLE_CARDS, compareByDateDesc } from './OrdersSection.utils'
import { useSaleCards } from './composables/useSaleCards'
import { useTradeInCards } from './composables/useTradeInCards'

const i18n = useI18n()
const { features } = useMarketplace()
const SEE_MORE_IMAGE_SRC = '/img/help-center/see-more-items.png'
const resolveLocalizedRoute = useRouteLocationWithLocale()
const protectionPlansTo = resolveLocalizedRoute({
  type: 'internal',
  name: ROUTE_NAMES.DASHBOARD.SERVICES_HUB,
})
const ordersPageTo = resolveLocalizedRoute({
  type: 'internal',
  name: ROUTE_NAMES.DASHBOARD.MY_ORDERS.SELF,
})
const tradeInsPageTo = resolveLocalizedRoute({
  type: 'internal',
  name: ROUTE_NAMES.DASHBOARD.MY_SALES,
})

const {
  salesCards,
  hasSales,
  hasMoreSales,
  isPending: isSalesPending,
  status: salesStatus,
} = useSaleCards()
const {
  tradeInCards,
  hasMoreTradeIns,
  isTradeInEnabled,
  isPending: isTradeInsPending,
  status: tradeInsStatus,
} = useTradeInCards()

const isPending = computed(
  () => isSalesPending.value || isTradeInsPending.value,
)

type CardType = 'sale' | 'trade-in'
type ItemFilter = 'all' | 'purchases' | 'trade-ins' | 'protection-plans'

const FILTER_TO_CARD_TYPE: Record<
  Exclude<ItemFilter, 'all' | 'protection-plans'>,
  CardType
> = {
  purchases: 'sale',
  'trade-ins': 'trade-in',
}

type FilterOption = {
  value: ItemFilter
  label: (typeof translations)[keyof typeof translations]
}

const ITEM_FILTERS = computed<FilterOption[]>(() => [
  { value: 'all', label: translations.filterAll },
  { value: 'purchases', label: translations.filterPurchases },
  ...(isTradeInEnabled
    ? [{ value: 'trade-ins', label: translations.filterTradeIns } as const]
    : []),
  ...(features.insurances.enabled
    ? [
        {
          value: 'protection-plans',
          label: translations.filterProtectionPlans,
        } as const,
      ]
    : []),
])

const selectedFilter = ref<ItemFilter>('all')

const shopTo = resolveLocalizedRoute({ name: HOME })
const tradeInTo = resolveLocalizedRoute({
  name: CMS.BUYBACK,
  params: { pageName: 'home' },
})
const fallbackState = computed(() => {
  const includesPurchases =
    selectedFilter.value === 'all' || selectedFilter.value === 'purchases'
  const includesTradeIns =
    selectedFilter.value === 'all' || selectedFilter.value === 'trade-ins'

  if (includesPurchases && salesStatus.value === 'error') {
    return {
      title: translations.ordersErrorTitle,
      description: translations.ordersErrorDescription,
      cta: translations.ordersErrorCta,
      to: ordersPageTo,
    }
  }

  if (includesTradeIns && tradeInsStatus.value === 'error') {
    return {
      title: translations.tradeInsErrorTitle,
      description: translations.tradeInsErrorDescription,
      cta: translations.tradeInsErrorCta,
      to: tradeInsPageTo,
    }
  }

  return selectedFilter.value === 'trade-ins'
    ? {
        title: translations.noTradeInsTitle,
        description: translations.noTradeInsDescription,
        cta: translations.noTradeInsCta,
        to: tradeInTo,
      }
    : {
        title: translations.noOrdersTitle,
        description: translations.noOrdersDescription,
        cta: translations.noOrdersCta,
        to: shopTo,
      }
})

const mappedOrderlines = computed(() =>
  [...salesCards.value, ...tradeInCards.value].sort(compareByDateDesc),
)

const filteredOrderlines = computed(() => {
  if (selectedFilter.value === 'all') return mappedOrderlines.value
  if (selectedFilter.value === 'protection-plans') return []

  const cardType = FILTER_TO_CARD_TYPE[selectedFilter.value]

  return mappedOrderlines.value.filter((card) => card.type === cardType)
})

// Limit the selected tab, keeping any purchase replacement chain at the cutoff
// together. A purchase crowded out of All can still appear on Purchases.
const visibleOrderlines = computed(() => {
  const cards = filteredOrderlines.value
  const boundaryCard = cards[MAX_VISIBLE_CARDS - 1]

  if (boundaryCard?.type !== 'sale') return cards.slice(0, MAX_VISIBLE_CARDS)

  let end = MAX_VISIBLE_CARDS
  let nextCard = cards[end]

  while (
    nextCard?.type === 'sale' &&
    nextCard.groupKey === boundaryCard.groupKey
  ) {
    end += 1
    nextCard = cards[end]
  }

  return cards.slice(0, end)
})

const showSeeMore = computed(() => {
  const hasOmittedCards =
    filteredOrderlines.value.length > visibleOrderlines.value.length
  const hasSalesWithoutCards = hasSales.value && !salesCards.value.length

  switch (selectedFilter.value) {
    case 'all':
      return (
        hasOmittedCards ||
        hasMoreSales.value ||
        hasMoreTradeIns.value ||
        hasSalesWithoutCards
      )
    case 'purchases':
      return hasOmittedCards || hasMoreSales.value || hasSalesWithoutCards
    case 'trade-ins':
      return hasOmittedCards || hasMoreTradeIns.value
    default:
      return false
  }
})

const seeMoreTo = computed(() =>
  selectedFilter.value === 'trade-ins' ? tradeInsPageTo : ordersPageTo,
)
</script>
